import { ApiResult, HttpClient } from "../http.js";
import {
  KlsEvaluationDto,
  KlsEvaluationStatus,
  KlsEvaluationCategory,
  KlsImprovementActionPriority,
  KlsImprovementActionStatus,
} from "../types/kls-evaluations.js";

export interface KlsEvaluationSectionPayload {
  id: string;
  name: string;
  category: KlsEvaluationCategory;
  rating: number;
  comments: string;
  findings?: string | null;
}

export interface KlsImprovementActionPayload {
  id: string;
  description: string;
  priority: KlsImprovementActionPriority;
  responsibleUserId?: string | null;
  responsibleUserName?: string | null;
  dueDate?: string | null;
  status: KlsImprovementActionStatus;
  completedAt?: string | null;
}

export interface KlsYearStatsPayload {
  totalInspections: number;
  approvedInspections: number;
  failedInspections: number;
  totalDeviations: number;
  openDeviations: number;
  resolvedDeviations: number;
  equipmentCalibrated: number;
  equipmentOverdue: number;
  competencyReviews: number;
}

export interface CreateKlsEvaluationPayload {
  year: number;
  evaluationDate: string;
  evaluatorId: string;
  evaluatorName: string;
  status: KlsEvaluationStatus;
  sections?: KlsEvaluationSectionPayload[] | null;
  overallRating: number;
  summary: string;
  strengths?: string[] | null;
  weaknesses?: string[] | null;
  improvementActions?: KlsImprovementActionPayload[] | null;
  previousActionsReview?: string | null;
  lastExternalAuditDate?: string | null;
  nextExternalAuditDate?: string | null;
  kontrolinstansName?: string | null;
  stats?: KlsYearStatsPayload | null;
  completedAt?: string | null;
  attachments?: string[] | null;
  id?: string;
}

export interface UpdateKlsEvaluationPayload {
  year: number;
  evaluationDate: string;
  evaluatorId: string;
  evaluatorName: string;
  status: KlsEvaluationStatus;
  sections?: KlsEvaluationSectionPayload[] | null;
  overallRating: number;
  summary: string;
  strengths?: string[] | null;
  weaknesses?: string[] | null;
  improvementActions?: KlsImprovementActionPayload[] | null;
  previousActionsReview?: string | null;
  lastExternalAuditDate?: string | null;
  nextExternalAuditDate?: string | null;
  kontrolinstansName?: string | null;
  stats?: KlsYearStatsPayload | null;
  completedAt?: string | null;
  attachments?: string[] | null;
}

export interface ListKlsEvaluationsQuery {
  year?: number;
  status?: KlsEvaluationStatus;
}

export class KlsEvaluationsApi {
  constructor(private readonly http: HttpClient) {}

  list(query: ListKlsEvaluationsQuery = {}): Promise<ApiResult<KlsEvaluationDto[]>> {
    return this.http.get<KlsEvaluationDto[]>("/api/kls-evaluations", { query: { ...query } });
  }

  get(id: string): Promise<ApiResult<KlsEvaluationDto>> {
    return this.http.get<KlsEvaluationDto>(`/api/kls-evaluations/${encodeURIComponent(id)}`);
  }

  create(payload: CreateKlsEvaluationPayload): Promise<ApiResult<string>> {
    return this.http.post<string>("/api/kls-evaluations", { body: payload });
  }

  update(id: string, payload: UpdateKlsEvaluationPayload): Promise<ApiResult<KlsEvaluationDto>> {
    return this.http.put<KlsEvaluationDto>(`/api/kls-evaluations/${encodeURIComponent(id)}`, { body: payload });
  }

  remove(id: string): Promise<ApiResult<string>> {
    return this.http.delete<string>(`/api/kls-evaluations/${encodeURIComponent(id)}`);
  }
}
